// src/useGameSession.js
// FSM-aware polling hook. Polls /state every 2s.
// Stops polling on terminal states (COMPLETED, CANCELLED).

import { useState, useEffect, useRef, useCallback } from 'react';

const API  = process.env.REACT_APP_API_BASE || 'https://api.binko.me/api';
const POLL = 2000;

// Polling stops on these states.
// COMPLETED is terminal for the poller — the restart flow works through
// nextSessionId returned in the final /state response, not continued polling.
// The game component calls restart() directly when the countdown fires,
// which does not depend on the poller running.
const TERMINAL = new Set(['CANCELLED']);

export function useGameSession(sessionId) {
  const [session,  setSession]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const lastUpdatedAt = useRef(null);
  const pollRef       = useRef(null);
  const tokenRef      = useRef(localStorage.getItem('binko_token'));

  const fetchState = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res  = await fetch(`${API}/games/${sessionId}/state`, {
        headers: { Authorization: `Bearer ${tokenRef.current}` }
      });
      const data = await res.json();

      // Timer expiry response — treat as state update
      if (!res.ok && data.fsm_state) {
        setSession(prev => ({ ...prev, fsm_state: data.fsm_state }));
        clearInterval(pollRef.current);
        return;
      }
      if (!res.ok) { setError(data.error); return; }

      // Always update — equality guard was preventing timer effects from re-firing
      lastUpdatedAt.current = data.updatedAt;
      setSession(data);
      setError(null);

      if (TERMINAL.has(data.fsm_state)) {
        clearInterval(pollRef.current);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    tokenRef.current = localStorage.getItem('binko_token');
    fetchState();
    pollRef.current = setInterval(fetchState, POLL);
    return () => clearInterval(pollRef.current);
  }, [sessionId, fetchState]);

  // ── Actions ────────────────────────────────────────────────────────────────

  const post = useCallback(async (path, body = {}) => {
    try {
      const res  = await fetch(`${API}/games/${sessionId}${path}`, {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization:  `Bearer ${tokenRef.current}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) return { error: data.error };

      setSession(prev => ({
        ...prev,
        ...data,
        updatedAt: new Date().toISOString(),
      }));
      lastUpdatedAt.current = new Date().toISOString();

      // Resume polling if moved to non-terminal
      if (data.fsm_state && !TERMINAL.has(data.fsm_state)) {
        clearInterval(pollRef.current);
        pollRef.current = setInterval(fetchState, POLL);
      }
      // Stop polling if terminal
      if (data.fsm_state && TERMINAL.has(data.fsm_state)) {
        clearInterval(pollRef.current);
      }

      return { ok: true, data };
    } catch (err) {
      return { error: err.message };
    }
  }, [sessionId, fetchState]);

  const submitMove   = useCallback((playerId, moveData) => post('/move',          { moveData }),          [post]);
  const submitSkip   = useCallback((playerId)           => post('/skip',          {}),                   [post]);
  const confirmReady = useCallback((playerId)           => post('/confirm-ready', {}),                   [post]);
  const resign       = useCallback((playerId)           => post('/resign',        {}),                   [post]);
  const restart      = useCallback(async (gameType, mode) => {
    const result = await post('/restart', { gameType, restartMode: mode || 'extend' });
    if (result?.ok) return result.data?.sessionId ?? null;
    return null;
  }, [post]);


 return {
    session,
    loading,
    error,
    submitMove,
    submitSkip,
    confirmReady,
    resign,
    restart,
    refetch: fetchState,
  };
}

// ─── Invite flow hook ─────────────────────────────────────────────────────────
export function useGameInvite() {
  const [error, setError] = useState(null);
  const token = localStorage.getItem('binko_token');

  const proposeGame = async ({ matchId, gameType, invitedPlayer }) => {
    setError(null);
    try {
      const res  = await fetch(`${API}/games/invite`, {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization:  `Bearer ${token}`,
        },
        body: JSON.stringify({ matchId, gameType, invitedPlayer }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error); return null; }
      return data.sessionId;
    } catch (err) {
      setError(err.message); return null;
    }
  };

  const acceptInvite = async (sessionId) => {
    const res  = await fetch(`${API}/games/${sessionId}/accept`, {
      method:  'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization:  `Bearer ${token}`,
      },
      body: JSON.stringify({}),
    });
    const data = await res.json();
    return res.ok ? { ok: true, ...data } : { error: data.error };
  };

  const rejectInvite = async (sessionId) => {
    const res = await fetch(`${API}/games/${sessionId}/reject`, {
      method:  'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization:  `Bearer ${token}`,
      },
      body: JSON.stringify({}),
    });
    return res.ok;
  };

  return { proposeGame, acceptInvite, rejectInvite, error };
}
